<template>
	<div :class="`${full ? 'grow' : ''} relative`">
		<div class="flex items-end gap-3">
			<LabeledObject full required>
				<template #label>{{ trans(label) }}</template>
				<input v-model="term" type="text" @keyup.enter.stop="search" @keyup.stop="clear">
			</LabeledObject>
			<PrimaryButton :color="Color.Blue" @click.stop="search">
				<i class="fa-solid fa-search"/>
			</PrimaryButton>
		</div>
		<SearchResults v-if="results.length > 0 || searching">
			<SearchResultItem v-if="searching">
				<Searching/>
			</SearchResultItem>
			<SearchResultItem v-for="res in results" v-else @click.stop="select(res)">
				<LocationView :location="res"/>
			</SearchResultItem>
		</SearchResults>
	</div>
	<div v-if="location" class="w-full px-2 py-1 flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-gray-900 rounded">
		<LocationView :location="location"/>
		<SoftButton :color="Color.Red" @click.stop="unselect">
			<i class="fa-solid fa-trash"/>
		</SoftButton>
	</div>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {type Ref, ref} from 'vue'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import {type Country} from '@/Types/Country'
import {type Location} from '@/Types/Location'
import Searching from '@/Components/UI/Searching.vue'
import LocationView from '@/Components/UI/Location.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import SearchResults from '@/Components/Search/SearchResults.vue'
import SearchResultItem from '@/Components/Search/SearchResultItem.vue'
import {showInfo, showSuccess, showValidationErrors, showWarning} from '@/Helpers/general'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'

const props = defineProps<{
	label:string,
	countries?:Array<Country>,
	locationId?:number,
	location?:Location,
	localOnly?:boolean,
	required?:boolean,
	full?:boolean,
}>()

const emit = defineEmits([
	'update:locationId',
])

const term:Ref<string> = ref('')
const label:Ref<string> = ref(props.label)
const searching:Ref<boolean> = ref(false)
const results:Ref<Array<Location>> = ref([])
const localOnly:Ref<boolean> = ref(props.localOnly)
const countries:Ref<Array<Country>> = ref(props.countries ?? [])
const location:Ref<Location|undefined> = ref(props.location)
const locationId:Ref<number|undefined> = ref(props.locationId)

function search():void{
	if(!searching.value){
		if(countries.value.length === 0){
			showWarning(trans('account.exceptions.no_citizenship'))
		} else {
			searching.value = true
			results.value = []
			showInfo(trans('general.searching'))
			axios.post(route('search.index', 'locations'), {
				countries: countries.value.map(country => country.id),
				local_only: localOnly.value ? 1 : 0,
				term: term.value,
			}).then(({data}) => {
				results.value = data
				showSuccess(trans('general.shown'))
			}).catch(showValidationErrors).finally(() => {
				searching.value = false
			})
		}
	}
}

function select(selectedLocation:Location):void{
	emit('update:locationId', selectedLocation.id)
	locationId.value = selectedLocation.id
	location.value = selectedLocation
	term.value = ''
	results.value = []
}

function unselect():void{
	emit('update:locationId', null)
	locationId.value = undefined
	location.value = undefined
}

function clear():void{
	if(term.value.trim().length === 0){
		results.value = []
	}
}
</script>